<template>
  <div class="scan-loading-order-detail-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">装车扫描</h1>
    </header>

    <main class="content">
      <!-- 状态提示横幅 -->
      <div v-if="statusMsg" class="status-banner" :class="statusType">
        {{ statusMsg }}
      </div>

      <!-- 扫码输入区 -->
      <div class="form-section source-section">
        <h3 class="section-title">扫码输入</h3>
        <div class="form-group">
          <input 
            id="sourceDataInput"
            type="text" 
            v-model="sourceData" 
            placeholder="扫描数据后自动解析（格式：物料编码*数量*供应商编码*...）"
            @input="handleInputChange"
            autocomplete="off"
            :disabled="isSubmitted"
          />
        </div>
        
        <!-- 操作按钮组 -->
        <div class="source-actions">
          <button @click="handleSourceParse" class="btn parse-btn" :disabled="isParsing || isSubmitted">
            {{ isParsing ? '解析中...' : '解析装车' }}
          </button>
          <button @click="openManualInput" class="btn manual-input-btn" :disabled="isSubmitted">
            手动输入
          </button>
          <button @click="clearSourceData" class="btn clear-btn" :disabled="isSubmitted">
            清空
          </button>
        </div>
        <p class="pda-hint">提示：扫描后自动解析</p>
      </div>
      
      <!-- 新增：汇总信息区域 -->
      <div v-if="!loading && !errorMsg" class="form-section summary-section">
        <h3 class="section-title">汇总信息</h3>
        <div class="summary-content">
          <div class="summary-item">
            <span class="summary-label">总件数：</span>
            <span class="summary-value">{{ totalItems }} 件</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">总数量：</span>
            <span class="summary-value">{{ totalQuantity.toFixed(2) }}</span>
          </div>
        </div>
      </div>
      
      <!-- 装车详情列表 -->
      <div class="form-section result-section">
        <h3 class="section-title">装车详情</h3>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载装车详情中...</p>
        </div>

        <!-- 错误提示 -->
        <div v-if="errorMsg" class="error-message">{{ errorMsg }}</div>

        <!-- 空状态 -->
        <div v-if="!loading && !errorMsg && loadingData.length === 0" class="empty-state">
          <div class="empty-icon">📦</div>
          <p class="empty-text">暂无装车数据</p>
        </div>

        <!-- 装车详情表格 -->
        <div v-else-if="!loading && !errorMsg && loadingData.length > 0" class="detail-table-container">
          <div class="table-header">
            <div class="table-col batch-col">批次号</div>
            <div class="table-col">供应商编码</div>
            <div class="table-col">物料编码</div>
            <div class="table-col">装车数量</div>
            <div class="table-col">操作</div>
          </div>
          <div class="table-body">
            <div 
              v-for="item in loadingData" 
              :key="item.id"
              class="table-row"
              :class="{ 'highlight-row': item.highlight }"
            >
              <div class="table-col batch-col">{{ item.batchNo }}</div>
              <div class="table-col">{{ item.supplierCode }}</div>
              <div class="table-col">{{ item.materialCode }}</div>
              <div class="table-col centered-content">
                {{ item.materialNum }}
              </div>
              <div class="table-col">
                <button 
                  class="btn"
                  :class="{ 'delete-btn-active': !isSubmitted, 'delete-btn-disabled': isSubmitted }"
                  @click.stop="deleteLoadingItem(item.id)"
                  :disabled="isSubmitted"
                  :title="isSubmitted ? '该装车单已提交，不可进行操作' : ''"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 完成装车按钮 -->
      <div class="bottom-actions">
        <button 
            @click="completeLoading" 
            class="btn complete-btn"
            :disabled="loadingData.length === 0 || isSubmitted"
          >
            完成装车
          </button>
        </div>
    </main>

    <!-- 手动输入弹窗 -->
    <div v-if="showManualInput" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">手动输入装车信息</h3>
          <button @click="closeManualInput" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="manualMaterialNo">物料编码</label>
            <input 
              id="manualMaterialNo" 
              v-model="manualInput.materialNo" 
              type="text" 
              placeholder="请输入物料编码"
              @input="trimInput('materialNo')"
              @blur="handleMaterialNoBlur"
              :disabled="isSubmitted"
            >
          </div>
          <div class="form-group">
            <label for="manualSupplierCode">供应商编码</label>
            <input 
              id="manualSupplierCode" 
              v-model="manualInput.supplierCode" 
              type="text" 
              placeholder="请输入供应商编码"
              @input="trimInput('supplierCode')"
              @blur="handleSupplierCodeBlur"
              :disabled="isSubmitted"
            >
          </div>
          <div class="form-group">
            <label for="manualBatchNo">批次号</label>
            <input 
              id="manualBatchNo" 
              v-model="manualInput.batchNo" 
              type="text" 
              placeholder="请输入批次号"
              @input="trimInput('batchNo')"
              :disabled="isSubmitted"
            >
          </div>
          <div class="form-group">
            <label for="manualQuantity">数量</label>
            <input 
              id="manualQuantity" 
              v-model="manualInput.materialNum" 
              type="number" 
              placeholder="请输入数量"
              min="1"
              :disabled="isSubmitted"
            >
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeManualInput" class="btn modal-cancel">取消</button>
          <button @click="confirmManualInput" class="btn modal-confirm" :disabled="!isManualInputValid || isSubmitted">
            确认添加
          </button>
        </div>
      </div>
    </div>

    <!-- 错误提示弹窗 -->
    <div v-if="showErrorModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ errorModalTitle }}</h3>
          <button @click="showErrorModal = false" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="error-message">{{ errorModalDetail }}</p>
          <p class="error-tip" v-if="errorModalAdvice">{{ errorModalAdvice }}</p>
        </div>
        <div class="modal-footer">
          <button @click="showErrorModal = false" class="btn modal-confirm">确定</button>
        </div>
      </div>
    </div>
    
    <!-- 新增：确认弹窗 -->
    <div v-if="showConfirmModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">确认完成装车</h3>
          <button @click="cancelCompleteLoading" class="modal-close" aria-label="关闭">×</button>
        </div>
        <div class="modal-body">
          <p class="confirm-message">您确定要完成装车吗？完成后将无法修改装车数据</p>
           
          <!-- 添加车牌号输入框 -->
          <div class="form-group">
            <label for="vehiclePlate">车牌号 <span class="required">*</span></label>
            <input 
              id="vehiclePlate"
              v-model="vehiclePlate"
              type="text" 
              placeholder="请输入车牌号"
              class="form-control"
              :disabled="loading || isSubmitted"
            />
          </div>
          
          <!-- 添加司机姓名输入框（非必填） -->
          <div class="form-group">
            <label for="driverName">司机姓名</label>
            <input 
              id="driverName"
              v-model="driverName"
              type="text" 
              placeholder="请输入司机姓名（选填）"
              class="form-control"
              :disabled="loading || isSubmitted"
            />
          </div>
          
          <!-- 添加司机电话输入框（非必填） -->
          <div class="form-group">
            <label for="driverPhone">司机电话</label>
            <input 
              id="driverPhone"
              v-model="driverPhone"
              type="text" 
              placeholder="请输入司机电话（选填）"
              class="form-control"
              :disabled="loading || isSubmitted"
            />
          </div>
        </div>
        <div class="modal-footer">
          <button @click="cancelCompleteLoading" class="btn modal-cancel">取消</button>
          <button @click="confirmCompleteLoading" class="btn modal-confirm" :disabled="loading || isSubmitted || !vehiclePlate.trim()">
            {{ loading ? '提交中...' : isSubmitted ? '已提交' : '确定' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getLoadingOrderDetail, updateLoadingOrder, deleteLoadingOrderDetail, submitLoadingOrder } from '@/api/loadingOrder'

export default {
  name: 'LoadingOrderDetail',
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loadingOrderId = route.params.id
    
    // 基础状态管理
    const sourceData = ref('') // 扫码输入内容
    const isParsing = ref(false) // 解析中状态
    const statusMsg = ref('') // 状态提示文本
    const statusType = ref('success') // 状态类型：success/error/warn
    const loadingData = ref([]) // 装车数据列表
    const loading = ref(false) // 加载状态
    const errorMsg = ref('') // 错误信息
    // 新增：装车单已提交状态
    const isSubmitted = ref(false)
    const vehiclePlate = ref('') // 新增：车牌号输入
    const driverName = ref('') // 新增：司机姓名
    const driverPhone = ref('') // 新增：司机电话

    // 弹窗控制
    const showManualInput = ref(false) // 手动输入弹窗
    const showErrorModal = ref(false) // 错误提示弹窗
    const showConfirmModal = ref(false) // 新增：确认弹窗

    // 错误弹窗内容
    const errorModalTitle = ref('提示')
    const errorModalDetail = ref('')
    const errorModalAdvice = ref('')


    // 手动输入表单
    const manualInput = ref({
      materialNo: '', // 物料编码
      supplierCode: '', // 供应商编码
      supplierId: '', // 供应商ID
      supplierName: '', // 供应商名称
      batchNo: '', // 批次号
      materialNum: 1 // 数量
    })

    // 音频上下文（提示音）
    let audioContext = null
    const isAudioContextActive = ref(false)

    // 解析计时器
    let parseTimer = null

    // 计算属性：手动输入表单验证
    const isManualInputValid = computed(() => {
      return manualInput.value.materialNo.trim() && 
             manualInput.value.supplierCode.trim() && 
             manualInput.value.batchNo.trim() && 
             manualInput.value.materialNum > 0
    })
    
    // 新增：计算属性 - 总件数和总数量
    const totalItems = computed(() => {
      return loadingData.value.length
    })
    
    const totalQuantity = computed(() => {
      return loadingData.value.reduce((sum, item) => {
        return sum + (parseFloat(item.materialNum) || 0)
      }, 0)
    })

    // 初始化音频上下文 - 与出库组件保持一致
    const initAudioContext = () => {
      if (audioContext || isAudioContextActive.value) return

      try {
        const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
        audioContext = new AudioContextConstructor()
        
        if (audioContext.state === 'suspended') {
          audioContext.resume().then(() => {
            isAudioContextActive.value = true
          }).catch(err => console.warn('激活音频上下文失败：', err))
        } else {
          isAudioContextActive.value = true
        }

        // 仅初始化一次
        document.removeEventListener('click', initAudioContext)
        document.removeEventListener('touchstart', initAudioContext)
      } catch (err) {
        console.warn('创建音频上下文失败：', err)
      }
    }

    // 播放成功提示音 - 与出库组件保持一致
    const playSuccessBeep = () => {
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(3000, audioContext.currentTime)
        gainNode.gain.setValueAtTime(1, audioContext.currentTime)

        oscillator.start()
        oscillator.stop(audioContext.currentTime + 0.1)
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放成功提示音失败：', error)
      }
    };

    // 播放错误提示音 - 与出库组件保持一致
    const playErrorBeep = () => {
      // 使用出库组件中的重复提示音作为错误提示音
      try {
        if (!audioContext) {
          const AudioContextConstructor = window.AudioContext || window.webkitAudioContext
          audioContext = new AudioContextConstructor()
        }

        if (audioContext.state === 'suspended') {
          audioContext.resume().catch(err => {
            console.warn('恢复音频上下文失败：', err)
            return
          })
        }

        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()
        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.type = 'sine'
        oscillator.frequency.setValueAtTime(2200, audioContext.currentTime)
        gainNode.gain.setValueAtTime(2, audioContext.currentTime)

        const beepInterval = 200
        const beepCount = 5
        for (let i = 0; i < beepCount; i++) {
          setTimeout(() => {
            gainNode.gain.setValueAtTime(2, audioContext.currentTime)
            setTimeout(() => {
              gainNode.gain.setValueAtTime(0, audioContext.currentTime)
            }, 100)
          }, i * beepInterval)
        }

        oscillator.start()
        oscillator.onended = () => {
          oscillator.disconnect()
          gainNode.disconnect()
        }
      } catch (error) {
        console.warn('播放错误提示音失败：', error)
      }
    };

    // 设置状态提示
    const setStatus = (message, type = 'success') => {
      statusMsg.value = message
      statusType.value = type
      
      // 3秒后自动清除提示
      setTimeout(() => {
        if (statusMsg.value === message) {
          statusMsg.value = ''
        }
      }, 3000)
    }

    // 获取装车详情数据
    const fetchLoadingDetails = async () => {
      try {
        loading.value = true;
        errorMsg.value = '';
        
        const response = await getLoadingOrderDetail(loadingOrderId);
        
        if (response.success && response.data) {
          // 修改：根据API返回数据结构正确处理
          // API返回的数据中，details数组包含了装车数据
          loadingData.value = response.data.details.map(item => ({
            ...item,
            highlight: false
          }));
          
          // 当status为1时，设置isSubmitted为true，禁用所有操作
      isSubmitted.value = response.data.status === 1;
      
      // if (isSubmitted.value) {
      //   setStatus('该装车单已提交，不可进行操作', 'warn');
      // }
        } else {
          errorMsg.value = response.msg || '获取装车详情失败';
        }
      } catch (error) {
        console.error('获取装车详情失败:', error);
        errorMsg.value = `获取失败：${error.message}`;
      } finally {
        loading.value = false;
      }
    }

    // 清空输入
    const clearSourceData = () => {
      sourceData.value = '';
      document.getElementById('sourceDataInput')?.focus();
    }

    // 返回上一页
    const handleBack = () => {
      router.back();
    }

    // 处理输入变化
    const handleInputChange = () => {
      // 自动解析逻辑
      if (parseTimer) {
        clearTimeout(parseTimer);
      }
      
      if (sourceData.value && sourceData.value.includes('*')) {
        parseTimer = setTimeout(() => {
          handleSourceParse();
        }, 500);
      }
    }

    // 处理扫码解析 - 核心修改：补充materialCode字段传递
    const handleSourceParse = async () => {
      // 如果已提交，不允许操作
      if (isSubmitted.value) {
        setStatus('该装车单已提交，不可进行操作', 'error');
        playErrorBeep();
        return;
      }
      
      if (!sourceData.value.trim()) {
        setStatus('请输入扫码数据', 'warn');
        return;
      }
      
      try {
        isParsing.value = true;
        
        // 解析扫码数据（格式为：物料编码*数量*供应商编码*...*生产订单号**批次号）
        const parts = sourceData.value.split('*');
        if (parts.length < 7) {
          throw new Error('数据格式不正确，请检查');
        }
        
        // 1. 解析物料编码（parts[0]即22001-000015），新增materialCode变量
        const materialCode = parts[0].trim(); // 物料编码（后端需要的字段名）
        const materialNo = parts[0].trim();   // 保持原有逻辑的物料编码字段
        const materialNum = parseInt(parts[1].trim());
        const supplierCode = parts[2].trim();
        const productionOrderNo = parts[parts.length - 3].trim();
        const batchNo = parts[parts.length - 1].trim();
        
        if (!materialCode || !materialNo || isNaN(materialNum) || !supplierCode || !batchNo) {
          throw new Error('数据内容不完整，请检查');
        }
        
        // 新增：检查当前列表中是否已存在相同批次号的项
        const isBatchExists = loadingData.value.some(item => item.batchNo === batchNo);
        if (isBatchExists) {
          errorModalTitle.value = '提示';
          errorModalDetail.value = '该批次号已存在，请勿重复扫码';
          showErrorModal.value = true;
          playErrorBeep();
          sourceData.value = '';
          return;
        }
        
        // 2. 调用update接口时，新增materialCode字段（与原有字段并列，不修改原有逻辑）
        const updateData = {
          id: loadingOrderId,
          details: [{
            materialCode: materialCode, // 新增：向后端传递物料编码（22001-000015）
            materialNo: materialNo,     // 保持原有字段，不影响历史逻辑
            materialNum: materialNum,
            supplierCode: supplierCode,
            batchNo: batchNo,
            productionOrderNo: productionOrderNo
          }]
        };
        
        const response = await updateLoadingOrder(updateData);
        
        if (response.success) {
          setStatus(`添加成功: 物料${materialNo} ${materialNum}件`, 'success');
          playSuccessBeep();
          await fetchLoadingDetails();
        } else {
          setStatus(`添加失败：${response.msg || '未知错误'}`, 'error');
          playErrorBeep();
        }
        
        sourceData.value = '';
      } catch (error) {
        setStatus(`解析失败：${error.message}`, 'error');
        playErrorBeep();
      } finally {
        isParsing.value = false;
      }
    }

    // 打开手动输入弹窗
    const openManualInput = () => {
      // 如果已提交，不允许操作
      if (isSubmitted.value) {
        setStatus('该装车单已提交，不可进行操作', 'error');
        playErrorBeep();
        return;
      }
      
      manualInput.value = {
        materialNo: '',
        supplierCode: '',
        supplierId: '',
        supplierName: '',
        batchNo: '',
        materialNum: 1
      };
      showManualInput.value = true;
    }

    // 关闭手动输入弹窗
    const closeManualInput = () => {
      showManualInput.value = false;
    }

    // 确认手动输入 - 可选：若手动输入也需传递materialCode，可参考扫码逻辑补充
    const confirmManualInput = async () => {
      // 如果已提交，不允许操作
      if (isSubmitted.value) {
        setStatus('该装车单已提交，不可进行操作', 'error');
        playErrorBeep();
        showManualInput.value = false;
        return;
      }
      
      if (!isManualInputValid.value) {
        setStatus('请完善所有必填信息', 'warn');
        return;
      }
      
      try {
        const { materialNo, batchNo, materialNum, supplierCode } = manualInput.value;
        
        // （可选）若手动输入需传递materialCode，可添加如下代码：
        // const materialCode = materialNo; // 手动输入时，物料编码与materialNo一致
        
        const updateData = {
          id: loadingOrderId,
          details: [{
            // materialCode: materialCode, // 手动输入场景如需传递，解开注释即可
            materialNo: materialNo,
            materialNum: materialNum,
            supplierCode: supplierCode,
            batchNo: batchNo
          }]
        };
        
        const response = await updateLoadingOrder(updateData);
        
        if (response.success) {
          setStatus(`添加成功: 物料${materialNo} ${materialNum}件`, 'success');
          playSuccessBeep();
          await fetchLoadingDetails();
          showManualInput.value = false;
        } else {
          setStatus(`添加失败：${response.msg || '未知错误'}`, 'error');
          playErrorBeep();
        }
      } catch (error) {
        setStatus(`添加失败：${error.message}`, 'error');
        playErrorBeep();
      }
    }

    // 修剪输入内容前后空格
    const trimInput = (field) => {
      if (manualInput.value[field]) {
        manualInput.value[field] = manualInput.value[field].trim();
      }
    }

    // 处理物料编码失焦
    const handleMaterialNoBlur = async () => {
      const { materialNo, supplierCode } = manualInput.value;
      if (materialNo.trim() && supplierCode.trim()) {
        try {
          // 预留：物料信息预查询逻辑
        } catch (error) {
          // 静默失败，不影响用户操作
        }
      }
    }

    // 处理供应商编码失焦
    const handleSupplierCodeBlur = async () => {
      // 预留：供应商信息预查询逻辑
    }

    // 更新数量
    const updateQuantity = async (id, newMaterialNum) => {
      if (newMaterialNum < 1) return;
      
      try {
        const item = loadingData.value.find(item => item.id === id);
        if (item) {
          item.materialNum = newMaterialNum;
          setStatus('数量已更新', 'success');
        }
      } catch (error) {
        console.error('更新数量失败:', error);
        setStatus(`更新数量失败：${error.message}`, 'error');
        playErrorBeep();
      }
    }

    // 删除装车项
    const deleteLoadingItem = async (id) => {
      // 如果已提交，不允许操作
      if (isSubmitted.value) {
        setStatus('该装车单已提交，不可进行操作', 'error');
        playErrorBeep();
        return;
      }
      
      try {
        console.log('删除id:', id);
        const response = await deleteLoadingOrderDetail(id);
        
        if (response && response.success) {
          await fetchLoadingDetails();
          setStatus('删除成功', 'success');
          playSuccessBeep();
        } else {
          throw new Error(response?.msg || '删除失败');
        }
      } catch (error) {
        console.error('删除失败:', error);
        setStatus(`删除失败：${error.message}`, 'error');
        playErrorBeep();
      }
    }

    // 完成装车
    const completeLoading = async () => {
      try {
        if (loadingData.value.length === 0) {
          // 对于简单的验证错误，仍然可以使用顶部提示
          setStatus('没有装车数据，无法完成装车', 'warn');
          return;
        }
        
        if (isSubmitted.value) {
          setStatus('该装车单已提交，不可重复提交', 'error');
          playErrorBeep();
          return;
        }
        
        // 清空所有输入字段
        vehiclePlate.value = '';
        driverName.value = '';
        driverPhone.value = '';
        // 显示确认弹窗
        showConfirmModal.value = true;
      } catch (error) {
        console.error('完成装车失败:', error);
        
        // 检测是否是"该装车单已提交"的错误
        if (error.message.includes('该装车单已提交')) {
          isSubmitted.value = true; // 标记为已提交状态
          setStatus(error.message, 'error');
        } else {
          // 不再使用setStatus，而是使用弹窗显示错误
          errorModalTitle.value = '提交失败';
          errorModalDetail.value = error.message;
          errorModalAdvice.value = '请检查装车单状态后重试';
          showErrorModal.value = true;
        }
        
        // 播放错误提示音
        playErrorBeep();
      }
    }
    
    // 确认完成装车
    const confirmCompleteLoading = async () => {
      try {
        // 增加状态检查
        if (isSubmitted.value) {
          showConfirmModal.value = false;
          setStatus('该装车单已提交，不可重复提交', 'error');
          playErrorBeep();
          return;
        }
        
        // 验证车牌号
        if (!vehiclePlate.value.trim()) {
          setStatus('请输入车牌号', 'warn');
          return;
        }
        
        showConfirmModal.value = false;
        loading.value = true;
        
        // 调用API完成装车 - 添加车牌号、司机姓名和司机电话参数
        const response = await submitLoadingOrder(loadingOrderId, {
          vehiclePlate: vehiclePlate.value.trim(),
          driverName: driverName.value.trim(), // 司机姓名
          driverPhone: driverPhone.value.trim() // 司机电话
        });
        
        if (response.success) {
          // 获取返回的车次信息
          const message = response.msg || '装车完成！';
          setStatus(message, 'success');
          playSuccessBeep();
          
          // 延迟返回列表页
          setTimeout(() => {
            router.back();
          }, 1500);
        } else {
          // 使用错误弹窗显示错误信息
          throw new Error(response.msg || '提交装车单失败');
        }
      } catch (error) {
        console.error('完成装车失败:', error);
        
        // 检测是否是"该装车单已提交"的错误
        if (error.message.includes('该装车单已提交')) {
          isSubmitted.value = true; // 标记为已提交状态
          setStatus(error.message, 'error');
        } else {
          // 不再使用setStatus，而是使用弹窗显示错误
          errorModalTitle.value = '提交失败';
          errorModalDetail.value = error.message;
          errorModalAdvice.value = '请检查装车单状态后重试';
          showErrorModal.value = true;
        }
        
        // 播放错误提示音
        playErrorBeep();
      } finally {
        loading.value = false;
      }
    };
    
    // 新增：取消完成装车
    const cancelCompleteLoading = () => {
      showConfirmModal.value = false;
    }

    // 组件挂载时初始化
    onMounted(() => {
      document.addEventListener('click', initAudioContext)
      document.addEventListener('touchstart', initAudioContext)
      
      nextTick(() => {
        document.getElementById('sourceDataInput')?.focus()
      })
      
      fetchLoadingDetails()
    })

    // 组件卸载时清理
    onUnmounted(() => {
      if (parseTimer) {
        clearTimeout(parseTimer)
      }
      
      if (audioContext) {
        audioContext.close()
      }
    })

    return {
      // 状态
      sourceData,
      isParsing,
      statusMsg,
      statusType,
      loadingData,
      loading,
      errorMsg,
      showManualInput,
      showErrorModal,
      showConfirmModal,
      errorModalTitle,
      errorModalDetail,
      errorModalAdvice,
      manualInput,
      isManualInputValid,
      isSubmitted,
      totalItems, // 导出总件数
      totalQuantity, // 导出总数量
      vehiclePlate, // 导出车牌号变量
      driverName, // 添加：导出司机姓名变量
      driverPhone,
      
      // 方法
      handleInputChange,
      handleSourceParse,
      clearSourceData,
      handleBack,
      openManualInput,
      closeManualInput,
      confirmManualInput,
      trimInput,
      handleMaterialNoBlur,
      handleSupplierCodeBlur,
      updateQuantity,
      deleteLoadingItem,
      completeLoading,
      confirmCompleteLoading, // 导出确认完成装车方法
      cancelCompleteLoading // 导出取消完成装车方法
    }
  }
}
</script>

<style>
/* 基础样式 */
.scan-loading-order-detail-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  background-color: #f5f7fa;
  color: #333;
  font-size: 14px;
}

/* 头部样式 */
.header {
  display: flex;
  align-items: center;
  padding: 3px 16px;
  background-color: #032566;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: sticky;
  top: 0;
  z-index: 100;
}

.back-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.icon-back {
  width: 24px;
  height: 24px;
}

.title {
  flex: 1;
  font-size: 18px;
  text-align: center;
  margin: 0;
  font-weight: 600;
}

/* 内容区域样式 */
.content {
  flex: 1;
  padding: 12px;
  overflow-y: auto;
  box-sizing: border-box;
  padding-bottom: 20px;
}

/* 表单区域样式 */
.form-section {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.section-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.section-title::before {
  content: '';
  width: 4px;
  height: 18px;
  background-color: #1890ff;
  margin-right: 8px;
  border-radius: 2px;
}

.form-group {
  margin-bottom: 16px;
}

.form-group input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 8px;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  min-height: 44px;
}

.form-group input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

/* 按钮样式 */
.btn {
  min-height: 44px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.source-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.parse-btn {
  background-color: #1890ff;
  color: #fff;
  flex: 1;
}

.parse-btn:hover:not(:disabled) {
  background-color: #40a9ff;
}

.parse-btn:disabled {
  background-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

.manual-input-btn {
  background-color: #52c41a;
  color: #fff;
  flex: 1;
}

.manual-input-btn:hover {
  background-color: #73d13d;
}

.clear-btn {
  background-color: #f5f5f5;
  color: #666;
  min-width: 80px;
}

.clear-btn:hover {
  background-color: #e8e8e8;
}

.pda-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #999;
  text-align: center;
}

/* 状态提示横幅 */
.status-banner {
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 16px;
  text-align: center;
  font-weight: 500;
}

.status-banner.success {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.status-banner.error {
  background-color: #fff2f0;
  color: #ff4d4f;
  border: 1px solid #ffccc7;
}

.status-banner.warn {
  background-color: #fffbe6;
  color: #faad14;
  border: 1px solid #ffe58f;
}

/* 加载状态 */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 0;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #1890ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  margin-top: 12px;
  color: #666;
}

/* 错误提示 */
.error-message {
  padding: 16px;
  background-color: #fff2f0;
  color: #ff4d4f;
  border-radius: 8px;
  text-align: center;
  border: 1px solid #ffccc7;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  color: #999;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 14px;
}

/* 表格样式 */
.detail-table-container {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e8e8e8;
}

.table-header {
  display: flex;
  background-color: #fafafa;
  border-bottom: 1px solid #e8e8e8;
  font-weight: 600;
}

/* 数量居中显示样式 */
.centered-content {
  display: flex;
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
  height: 100%;
}

/* 确保表格单元格有足够的高度来容纳内容 */
.table-col {
  flex: 1;
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: center;
}

.batch-col {
  min-width: 120px;
}

.table-body {
  max-height: 400px;
  overflow-y: auto;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
  transition: background-color 0.2s;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background-color: #f5f5f5;
}

.highlight-row {
  background-color: #e6f7ff !important;
  animation: highlightFade 2s ease-out;
}

@keyframes highlightFade {
  0% { background-color: #bae7ff; }
  100% { background-color: #e6f7ff; }
}

/* 数量控制 */
.quantity-control {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

/* 数量显示样式 */
.quantity-display {
  padding: 6px 12px;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
  min-width: 60px;
  display: inline-block;
}

.quantity-btn {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  border: 1px solid #dcdfe6;
  background-color: #fff;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.quantity-btn:hover:not(:disabled) {
  color: #1890ff;
  border-color: #1890ff;
}

.quantity-btn:disabled {
  color: #bfbfbf;
  border-color: #dcdfe6;
  cursor: not-allowed;
}

.quantity-input {
  width: 60px;
  height: 28px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  text-align: center;
  font-size: 14px;
  outline: none;
}

.quantity-input:focus {
  border-color: #1890ff;
}

/* 活跃状态的删除按钮 - 红色 */
  .delete-btn-active {
    background-color: #ff4d4f;
    color: #fff;
    min-width: 60px;
    height: 28px;
    font-size: 12px;
  }

  .delete-btn-active:hover {
    background-color: #ff7875;
  }

  /* 禁用状态的删除按钮 - 灰色 */
  .delete-btn-disabled {
    background-color: rgb(217, 217, 217);
    color: #fff;
    min-width: 60px;
    height: 28px;
    font-size: 12px;
    cursor: not-allowed;
  }

/* 底部操作按钮 */
.bottom-actions {
  margin-top: 16px;
  padding: 0 12px;
  position: sticky;
  bottom: 0;
  background-color: #f5f7fa;
  padding-bottom: 20px;
  z-index: 50;
}

.complete-btn {
  width: 100%;
  background-color: #52c41a;
  color: #fff;
  font-size: 16px;
  height: 48px;
}

.complete-btn:hover:not(:disabled) {
  background-color: #73d13d;
}

.complete-btn:disabled {
  background-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

/* 弹窗样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 400px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid #e8e8e8;
}

.modal-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.modal-close:hover {
  background-color: #f5f5f5;
  color: #666;
}

.modal-body {
  padding: 16px;
  overflow-y: auto;
}

.modal-footer {
  display: flex;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid #e8e8e8;
}

.modal-cancel {
  flex: 1;
  background-color: #f5f5f5;
  color: #666;
}

.modal-cancel:hover {
  background-color: #e8e8e8;
}

.modal-confirm {
  flex: 1;
  background-color: #1890ff;
  color: #fff;
}

.modal-confirm:hover:not(:disabled) {
  background-color: #40a9ff;
}

.modal-confirm:disabled {
  background-color: #d9d9d9;
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

.error-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #999;
  }

  /* 新增：确认消息样式 */
  .confirm-message {
    text-align: center;
    padding: 8px 0;
    color: #333;
  }

  /* 响应式适配 */
@media (max-width: 480px) {
  .header {
    padding: 3px 12px;
  }
  
  .title {
    font-size: 16px;
  }
  
  .content {
    padding: 8px;
  }
  
  .form-section {
    padding: 12px;
    margin-bottom: 12px;
  }
  
  .section-title {
    font-size: 14px;
  }
  
  .form-group input {
    padding: 10px 12px;
    font-size: 13px;
  }
  
  .source-actions {
    gap: 6px;
  }
  
  .btn {
    min-height: 40px;
    font-size: 13px;
  }
  
  .table-col {
    padding: 8px 4px;
    font-size: 12px;
  }
  
  .batch-col {
    min-width: 100px;
  }
  
  .bottom-actions {
    padding: 0 8px 16px;
  }
  
  .complete-btn {
    height: 44px;
    font-size: 14px;
  }
  
  .modal-container {
    width: 95%;
  }
}

/* 触摸设备优化 */
@media (hover: none) {
  .back-btn:hover {
    background: transparent;
  }
  
  .btn:active {
    opacity: 0.7;
  }
  
  .table-row:hover {
    background-color: transparent;
  }
}

/* 必填项标记样式 */
.required {
  color: #ff4d4f;
}

/* 滚动条样式 */
.table-body::-webkit-scrollbar {
  width: 6px;
}

.table-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.table-body::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-body::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 上下左右居中显示 */
.centered-content {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 批次号完整显示并支持换行 */
.batch-col {
  min-width: 100px;
  white-space: normal !important;
  word-break: break-all;
}

/* 确保表格单元格内容居中 */
.table-col {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  font-size: 12px;
  min-height: 40px;
}

/* 响应式调整 */
  @media (max-width: 480px) {
    .table-col {
      min-height: 48px;
      font-size: 11px;
    }
    
    .batch-col {
      min-width: 80px;
    }
  }

  /* 新增：汇总信息样式 */
  .summary-section {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .summary-content {
    display: flex;
    justify-content: space-around;
    padding: 8px 0;
  }

  .summary-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 12px 24px;
    background-color: #f6ffed;
    border-radius: 8px;
    border: 1px solid #b7eb8f;
  }

  .summary-label {
    font-size: 14px;
    color: #666;
    margin-bottom: 4px;
  }

  .summary-value {
    font-size: 24px;
    font-weight: 600;
    color: #52c41a;
  }

  /* 响应式适配 */
  @media (max-width: 480px) {
    .summary-content {
      flex-direction: column;
      gap: 8px;
    }
    
    .summary-item {
      padding: 10px 16px;
    }
    
    .summary-value {
      font-size: 20px;
    }
  }
</style>